import { useTable } from "@refinedev/core";
import { Link } from "react-router";

import { Badge } from "@/components/Badge";
import {
  ErrorBox,
  FilterBar,
  Loading,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
} from "@/components/Page";
import { EmptyRow, Table, Td, Th, Tr } from "@/components/Table";
import { INBOUND_STATUS_OPTIONS, formatDateTime, inboundStatus, plural } from "@/lib/format";
import { filterValue } from "@/lib/query";

// Same metadane. Ani urywka treści, ani adresu odbiorcy - ten drugi niósł
// w sobie token magic linku, czyli gotowe wejście na stronę narratora.
export interface InboundRow extends Record<string, unknown> {
  id: string;
  from_email: string;
  subject: string | null;
  attachments_count: number;
  status: string;
  created_at: string;
  prompt_delivery_id: string | null;
  story_id: string | null;
}

export function InboxListPage() {
  const { result, tableQuery, filters, setFilters, currentPage, setCurrentPage, pageSize } =
    useTable<InboundRow>({
      resource: "inbound-messages",
      sorters: { initial: [{ field: "created_at", order: "desc" }] },
    });

  const ustawFiltr = (field: string, value: string) =>
    setFilters([{ field, operator: "eq", value }], "merge");

  return (
    <>
      <PageHeader
        title="Skrzynka do przypisania"
        description="Wiadomości, których nie udało się powiązać z pytaniem. Zwykle to nie spam, tylko narrator, który odpisał z innego adresu - jego odpowiedź czeka tu na człowieka."
      />

      <FilterBar>
        <Select
          label="Status"
          value={filterValue(filters, "status")}
          emptyLabel="Czekające"
          options={INBOUND_STATUS_OPTIONS}
          onChange={(value) => ustawFiltr("status", value)}
        />
        <SearchInput
          label="Szukaj"
          placeholder="adres nadawcy, temat"
          value={filterValue(filters, "query")}
          onChange={(value) => ustawFiltr("query", value)}
        />
      </FilterBar>

      <ErrorBox error={tableQuery.error} />

      {tableQuery.isLoading ? (
        <Loading />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Przyszła</Th>
                <Th>Od</Th>
                <Th>Temat</Th>
                <Th>Załączniki</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {result.data.length === 0 && (
                <EmptyRow colSpan={5}>
                  Nic nie czeka na przypisanie. Każda odpowiedź trafiła tam, gdzie miała trafić.
                </EmptyRow>
              )}
              {result.data.map((message) => {
                const status = inboundStatus(message.status);
                return (
                  <Tr key={message.id}>
                    <Td nowrap>
                      <Link
                        to={`/skrzynka/${message.id}`}
                        className="text-accent underline underline-offset-4 hover:text-accent-dark"
                      >
                        {formatDateTime(message.created_at)}
                      </Link>
                    </Td>
                    <Td>{message.from_email}</Td>
                    <Td>{message.subject ?? "-"}</Td>
                    <Td nowrap>
                      {message.attachments_count === 0
                        ? "-"
                        : `${message.attachments_count} ${plural(
                            message.attachments_count,
                            "załącznik",
                            "załączniki",
                            "załączników",
                          )}`}
                    </Td>
                    <Td>
                      <Badge tone={status.tone}>{status.label}</Badge>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>

          <Pagination
            page={currentPage}
            pageSize={pageSize}
            total={result.total ?? 0}
            onChange={setCurrentPage}
          />
        </>
      )}
    </>
  );
}
